import React, { useMemo, useState } from "react";
import { X, Calendar, Repeat, Info } from "lucide-react";
import api from "@/lib/api";
import { toast } from "sonner";

const DAYS = [
    { iso: 1, short: "L", long: "Lundi" },
    { iso: 2, short: "M", long: "Mardi" },
    { iso: 3, short: "M", long: "Mercredi" },
    { iso: 4, short: "J", long: "Jeudi" },
    { iso: 5, short: "V", long: "Vendredi" },
    { iso: 6, short: "S", long: "Samedi" },
    { iso: 7, short: "D", long: "Dimanche" },
];

const QUICK_PRESETS = [
    { key: "weekend", label: "Week-ends (S+D)", days: [6, 7] },
    { key: "weekdays", label: "Semaine (L→V)", days: [1, 2, 3, 4, 5] },
    { key: "wed", label: "Chaque mercredi", days: [3] },
    { key: "sat", label: "Chaque samedi", days: [6] },
];

export default function RecurringMissionModal({ mission, onClose, onDone }) {
    const baseDate = mission.date;
    const [selected, setSelected] = useState(() => new Set());
    // Default until = base date + 8 weeks
    const defaultUntil = useMemo(() => {
        try {
            const d = new Date(baseDate);
            d.setDate(d.getDate() + 8 * 7);
            return d.toISOString().slice(0, 10);
        } catch {
            return baseDate;
        }
    }, [baseDate]);
    const [until, setUntil] = useState(defaultUntil);
    const [submitting, setSubmitting] = useState(false);

    const toggleDay = (iso) => {
        setSelected((prev) => {
            const next = new Set(prev);
            if (next.has(iso)) next.delete(iso);
            else next.add(iso);
            return next;
        });
    };

    const applyPreset = (days) => setSelected(new Set(days));

    // Compute estimated number of missions to create
    const estimatedCount = useMemo(() => {
        if (!selected.size || !until || !baseDate) return 0;
        try {
            const from = new Date(baseDate);
            const to = new Date(until);
            if (to <= from) return 0;
            let count = 0;
            const cursor = new Date(from);
            cursor.setDate(cursor.getDate() + 1);
            while (cursor <= to) {
                const dow = cursor.getDay() === 0 ? 7 : cursor.getDay(); // JS: 0=Sun, 1..6
                if (selected.has(dow)) count += 1;
                cursor.setDate(cursor.getDate() + 1);
            }
            return count;
        } catch {
            return 0;
        }
    }, [selected, until, baseDate]);

    const submit = async () => {
        if (selected.size === 0) {
            toast.error("Sélectionnez au moins un jour de la semaine");
            return;
        }
        if (!until) {
            toast.error("Renseignez une date de fin");
            return;
        }
        setSubmitting(true);
        try {
            const r = await api.post(`/missions/${mission.id}/duplicate-recurring`, {
                frequency: "weekly",
                days_of_week: Array.from(selected),
                until_date: until,
            });
            toast.success(`${r.data.created_count} mission(s) récurrente(s) créée(s) avec succès`);
            onDone && onDone(r.data);
        } catch (err) {
            toast.error(err?.response?.data?.detail || "Erreur lors de la création");
        } finally {
            setSubmitting(false);
        }
    };

    return (
        <div
            className="fixed inset-0 z-50 bg-black/60 backdrop-blur-sm flex items-center justify-center p-4"
            onClick={onClose}
            data-testid="recurring-modal"
        >
            <div
                onClick={(e) => e.stopPropagation()}
                className="bg-white rounded-2xl w-full max-w-lg shadow-2xl overflow-hidden"
            >
                <div className="p-5 border-b border-slate-100 flex items-start justify-between gap-3">
                    <div>
                        <div className="flex items-center gap-2 mb-1">
                            <div className="w-8 h-8 rounded-lg bg-[#F5A623] text-[#0B192C] flex items-center justify-center">
                                <Repeat size={16} />
                            </div>
                            <div className="overline">Missions récurrentes</div>
                        </div>
                        <div className="font-display text-xl">{mission.title}</div>
                        <div className="text-xs text-slate-500 mt-0.5">
                            À partir du {baseDate} · {mission.start_time}–{mission.end_time}
                        </div>
                    </div>
                    <button
                        onClick={onClose}
                        className="p-2 rounded hover:bg-slate-100 text-slate-500"
                        data-testid="recurring-modal-close"
                    >
                        <X size={18} />
                    </button>
                </div>

                <div className="p-5 space-y-5">
                    <div>
                        <div className="text-[11px] uppercase tracking-widest text-slate-500 mb-2">
                            Répéter les jours suivants
                        </div>
                        <div className="grid grid-cols-7 gap-2">
                            {DAYS.map((d) => (
                                <button
                                    key={d.iso}
                                    onClick={() => toggleDay(d.iso)}
                                    className={`aspect-square rounded-xl border-2 font-bold text-lg transition-colors ${
                                        selected.has(d.iso)
                                            ? "bg-[#0B192C] border-[#0B192C] text-white"
                                            : "bg-white border-slate-200 text-slate-600 hover:border-[#0B192C]"
                                    }`}
                                    title={d.long}
                                    data-testid={`recurring-day-${d.iso}`}
                                >
                                    {d.short}
                                </button>
                            ))}
                        </div>
                        <div className="flex flex-wrap gap-1.5 mt-3">
                            {QUICK_PRESETS.map((p) => (
                                <button
                                    key={p.key}
                                    onClick={() => applyPreset(p.days)}
                                    className="text-[11px] px-2.5 py-1 rounded-full bg-slate-100 hover:bg-[#F5A623]/20 text-slate-600 hover:text-[#0B192C] transition-colors"
                                    data-testid={`recurring-preset-${p.key}`}
                                >
                                    {p.label}
                                </button>
                            ))}
                        </div>
                    </div>

                    <div>
                        <label className="text-[11px] uppercase tracking-widest text-slate-500 mb-1 flex items-center gap-1">
                            <Calendar size={12} /> Jusqu'au (inclus)
                        </label>
                        <input
                            type="date"
                            value={until}
                            min={baseDate}
                            onChange={(e) => setUntil(e.target.value)}
                            className="w-full px-3 py-2.5 rounded-lg border border-slate-200 text-sm"
                            data-testid="recurring-until"
                        />
                        <div className="text-[11px] text-slate-500 mt-1">Période maximale : 12 mois</div>
                    </div>

                    <div className="rounded-xl bg-gradient-to-br from-[#F5A623]/10 to-[#F5A623]/5 border border-[#F5A623]/30 p-4">
                        <div className="flex items-start gap-2">
                            <Info size={16} className="text-[#F5A623] shrink-0 mt-0.5" />
                            <div className="text-sm text-slate-700 leading-relaxed">
                                <span className="font-bold text-[#0B192C] text-lg">
                                    {estimatedCount}
                                </span>{" "}
                                nouvelle{estimatedCount > 1 ? "s" : ""} mission{estimatedCount > 1 ? "s" : ""}{" "}
                                sera{estimatedCount > 1 ? "nt" : ""} créée{estimatedCount > 1 ? "s" : ""} et publiée{estimatedCount > 1 ? "s" : ""} en statut « ouvert »
                                pour que les candidats postulent.
                                {estimatedCount === 0 && (
                                    <div className="mt-1 text-xs text-slate-500">
                                        Sélectionnez au moins un jour et une date de fin.
                                    </div>
                                )}
                            </div>
                        </div>
                    </div>
                </div>

                <div className="p-5 border-t border-slate-100 flex justify-end gap-2 bg-slate-50/60">
                    <button
                        onClick={onClose}
                        disabled={submitting}
                        className="btn-ghost-navy text-sm"
                    >
                        Annuler
                    </button>
                    <button
                        onClick={submit}
                        disabled={submitting || estimatedCount === 0}
                        className="btn-navy text-sm disabled:opacity-50"
                        data-testid="recurring-submit"
                    >
                        {submitting ? "Création…" : `Créer ${estimatedCount} mission${estimatedCount > 1 ? "s" : ""}`}
                    </button>
                </div>
            </div>
        </div>
    );
}
